'use client';

import React, {FC, PropsWithChildren, useEffect, useState} from 'react';
import Head from 'next/head';
import Image from 'next/image';
import {useRouter} from 'next/navigation';

import {Button} from '@zoefin/design-system';

import {addToDataLayer} from '../../../utils/pageUtils';

import {useLandingPageContextValue} from './context/store';
import {LandingPageLayoutProps} from './index.d';

import './spacersStyles.module.scss';
import styles from './styles.module.scss';

interface MenuStructure {
  title: string;
  link: string;
  active: boolean;
  subMenu?: MenuStructure[];
}

const MENUS: MenuStructure[] = [
  {
    title: 'About',
    link: 'https://findanadvisor.com/about-us/',
    active: false,
  },
  {
    title: 'Financial Planning',
    link: 'https://findanadvisor.com/what-are-your-wealth-goals',
    active: false,
  },
  {
    title: 'Financial Advisors',
    link: 'https://findanadvisor.com/financial-advisor/',
    active: false,
  },
  {
    title: 'Resources',
    link: 'https://findanadvisor.com/what-are-your-wealth-goals',
    active: false,
  },
];

const LandingPageLayout: FC<PropsWithChildren<LandingPageLayoutProps>> = ({
  children,
  logoUrl = '/',
  headerType = 'full-header',
  background,
  phoneNumber = '(646) 680-9244',
}) => {
  const {push} = useRouter();

  const {containerInstance, isSticky, setIsSticky} =
    useLandingPageContextValue();

  const [menu, setMenu] = useState<MenuStructure[]>(MENUS);
  const [openMenu, setOpenMenu] = useState<boolean>(false);

  const toggleMenu = (index: number) => {
    const newMenu = [...menu];

    newMenu[index].active = !newMenu[index].active;
    setMenu(newMenu);
  };

  const scrollToTop = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const addToDataLayerHomePage = (section: string, label: string) => {
    addToDataLayer({
      overrideBase: 'Portal_Landing_Page',
      values: {
        Section: section,
        Label: label,
      },
    });
  };

  const redirectOnBoarding = (section: string, label: string) => {
    addToDataLayerHomePage(section, label);
    push('/onboarding');
  };

  const isFlatHeader = headerType === 'flat-header';

  const modifierLogoClass =
    logoUrl === null ? styles['homepage__container_logo--no-cursor'] : '';

  const contentBackgroundClass = background
    ? styles[`homepage__content_${background}`]
    : styles['homepage__content'];

  const navContainerClassName = openMenu
    ? styles['homepage__nav-container--open']
    : '';

  const navListClassName =
    isFlatHeader && styles['homepage__nav-list--flat-header'];

  useEffect(() => {
    const toggleSticky = () => {
      if (
        containerInstance.current.getBoundingClientRect().top < 0 &&
        !isSticky
      ) {
        setIsSticky(true);
      } else if (
        containerInstance.current.getBoundingClientRect().top >= 0 &&
        isSticky
      ) {
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', toggleSticky, {passive: true});

    return () => window.removeEventListener('scroll', toggleSticky);
  }, [isSticky, setIsSticky, containerInstance]);

  return (
    <>
      <Head>
        <title>
          Top Local Financial Advisors &amp; Planners - Zoe Financial
        </title>
        <meta
          name="description"
          content="Discover The Top Fiduciary Financial Advisors &amp; Planners! Create Your Financial Savings &amp; Investing Plan, Always Transparent Pricing. Schedule a free consult today!"
        />
      </Head>
      <main
        className={`${styles['homepage']} ${openMenu ? styles['homepage__menu-open'] : ''} ${
          isSticky ? styles['homepage__header--sticky'] : ''
        }`}
        ref={containerInstance}>
        <header className={styles['homepage__header']}>
          <div className={`${styles['homepage__container']} default-container`}>
            <div
              className={`${styles['homepage__container_logo']} ${modifierLogoClass}`}>
              <a href={logoUrl} className="dis-blo no-line-height">
                <picture>
                  <source
                    srcSet="/static/img/homepage/webp/logo.webp"
                    type="image/webp"
                  />
                  <source
                    srcSet="/static/img/homepage/logo.png"
                    type="image/jpeg"
                  />
                  <Image
                    src="/static/img/homepage/logo.png"
                    alt="Zoe Logo"
                    className="w-100"
                    width="86"
                    height="40"
                  />
                </picture>
              </a>
            </div>
            <div className={styles['homepage__container_content-menu']}>
              <div className={styles['homepage__menu']}>
                <button
                  type="button"
                  aria-label="Open menu"
                  data-testid="open-menu"
                  className={`${styles['homepage__menu_icon']} ${styles['homepage__menu_icon--open']}`}
                  onClick={() => setOpenMenu(true)}>
                  <Image
                    src="/static/svg/icon_bars.svg"
                    alt=""
                    width="32"
                    height="24"
                    unoptimized
                  />
                </button>

                <nav
                  className={`${styles['homepage__nav-container']} ${navContainerClassName}`}>
                  <button
                    type="button"
                    aria-label="Close menu"
                    className={`${styles['homepage__menu_icon']} ${styles['homepage__menu_icon--close']}`}
                    onClick={() => setOpenMenu(false)}>
                    <Image
                      src="/static/svg/icon_close.svg"
                      alt=""
                      width="20"
                      height="20"
                      unoptimized
                    />
                  </button>
                  <ul
                    className={`${styles['homepage__nav-list']} ${navListClassName}`}>
                    {menu.map(({title, link, subMenu, active}, index) => {
                      return (
                        <li
                          data-testid="items-menu"
                          key={link}
                          className={`${styles['homepage__nav-list_item']} ${
                            active
                              ? styles['homepage__nav-list_item--active']
                              : ''
                          }`}>
                          <a
                            data-testid="menu-link"
                            href={link}
                            className={styles['homepage__nav-list_item_link']}>
                            {title}
                          </a>
                          {!!subMenu?.length && (
                            <>
                              <button
                                type="button"
                                aria-label={`Toggle ${title} submenu`}
                                className={
                                  styles['homepage__nav-list_item_icon']
                                }
                                onClick={() => toggleMenu(index)}
                              />

                              <ul className="dropdown-menu">
                                {subMenu.map((sMenu, subindex) => {
                                  const indexSubMenu = `subMenu_${subindex}`;

                                  return (
                                    <li key={indexSubMenu}>
                                      <a
                                        className="dropdown-menu__link"
                                        href={sMenu.link}>
                                        {sMenu.title}
                                      </a>
                                    </li>
                                  );
                                })}
                              </ul>
                            </>
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  <div className={styles['homepage__nav-container_buttons']}>
                    {headerType === 'full-header' && (
                      <Button
                        config={{
                          color: 'blue',
                          type: 'ghost',
                        }}
                        className="ZUIButton--mobile ZUIButton--logIn"
                        onClick={() => {
                          push('/login');
                          addToDataLayerHomePage('Menu', 'Log In');
                        }}
                        as="button">
                        Log In
                      </Button>
                    )}

                    {isFlatHeader && (
                      <div
                        className={
                          styles['homepage__nav-container_info-number']
                        }>
                        <Image
                          src="/static/img/icons/icon_phone_blue.png"
                          alt="Phone Icon"
                          width={16}
                          height={16}
                        />
                        <p>{phoneNumber}</p>
                      </div>
                    )}

                    <Button
                      className="ZUIButton--mobile ZUIButton--getStarted"
                      onClick={() => redirectOnBoarding('Menu', 'Get Started')}
                      as="button">
                      Find An Advisor
                    </Button>
                  </div>
                </nav>
              </div>
            </div>
          </div>
        </header>
        <div className={contentBackgroundClass}>{children}</div>
        <footer className="footer">
          <div className="default-container">
            <div className="footer-menu">
              <div className="footer-menu__item">
                <ul>
                  <li data-testid="footer-li">
                    <a href="https://zoefinancial.com/press/">Press</a>
                  </li>
                  <li data-testid="footer-li">
                    <a href="https://findanadvisor.com/contact/">Contact Us</a>
                  </li>
                  <li data-testid="footer-li">
                    <a href="https://findanadvisor.com/faqs">FAQs</a>
                  </li>
                </ul>
              </div>
              <div className="footer-menu__item">
                <ul>
                  <li data-testid="footer-li">
                    <a href="https://findanadvisor.com/privacy-policy">
                      Privacy Policy
                    </a>
                  </li>
                  <li data-testid="footer-li">
                    <a href="https://findanadvisor.com/terms-of-use">
                      Terms of Use
                    </a>
                  </li>
                  <li data-testid="footer-li">
                    <a
                      href="https://findanadvisor.com/legal/form-adv-part-2a"
                      target="_blank"
                      rel="noreferrer">
                      Zoe ADV
                    </a>
                  </li>
                  <li data-testid="footer-li">
                    <a
                      href="https://findanadvisor.com/legal/zoe-financial-inc-customer-relationship-summary"
                      target="_blank"
                      rel="noreferrer">
                      Zoe CRS
                    </a>
                  </li>
                </ul>
              </div>
              <div className="footer-menu__item">
                <ul>
                  <li data-testid="footer-li">
                    <a href="https://findanadvisor.com/about-us/">About Zoe</a>
                  </li>
                  <li data-testid="footer-li">
                    <a href="https://zoefinancial.com/careers">Careers</a>
                  </li>
                </ul>
              </div>

              <div className="footer-menu__item footer-menu__item--media">
                <picture>
                  <source
                    srcSet="/static/img/homepage/webp/logo.webp"
                    type="image/webp"
                  />
                  <source
                    srcSet="/static/img/homepage/logo.png"
                    type="image/jpeg"
                  />
                  <Image
                    src="/static/img/homepage/logo.png"
                    alt="Zoe Logo"
                    className="only-desktop footer-logo"
                    width="100"
                    height="43"
                  />
                </picture>
                <ul>
                  <li data-testid="footer-li">
                    <a
                      href="https://www.instagram.com/zoefinancial/"
                      target="_blank"
                      rel="noreferrer">
                      <Image
                        src="/static/svg/icon-inst.svg"
                        alt="Contact us on Instagram"
                        width="21"
                        height="22"
                        unoptimized
                      />
                    </a>
                  </li>
                  <li data-testid="footer-li">
                    <a
                      href="https://www.facebook.com/zoefinofficial/"
                      target="_blank"
                      rel="noreferrer">
                      <Image
                        src="/static/svg/icon-fb.svg"
                        alt="Contact us on Facebook"
                        width="18"
                        height="21"
                        unoptimized
                      />
                    </a>
                  </li>
                  <li data-testid="footer-li">
                    <a
                      href="https://twitter.com/ZoeFinancial"
                      target="_blank"
                      rel="noreferrer">
                      <Image
                        src="/static/svg/icon-twitter.svg"
                        alt="Contact us on Twitter"
                        width="18"
                        height="21"
                        unoptimized
                      />
                    </a>
                  </li>
                  <li data-testid="footer-li">
                    <a
                      href="https://www.linkedin.com/company/zoe-financial-inc./"
                      target="_blank"
                      rel="noreferrer">
                      <Image
                        src="/static/svg/icon-linkedin.svg"
                        alt="Contact us on LinkedIn"
                        width="16"
                        height="21"
                        unoptimized
                      />
                    </a>
                  </li>
                </ul>
                <div className="footer-menu__item--media_contact">
                  <p>
                    <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
                  </p>
                  <p>
                    <a href="mailto:support@zoefin.com">support@zoefin.com</a>
                  </p>
                  <p>
                    <a href="https://zoefinancial.com" rel="noreferrer">
                      zoefinancial.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <div className="footer-copy">
              <div className="footer-copy__text">
                <h6>Ready to Grow Your Wealth?</h6>
                <p>
                  Copyright ©{' '}
                  <span suppressHydrationWarning>
                    {new Date().getFullYear()}
                  </span>{' '}
                  Zoe Financial, Inc. | All rights reserved
                </p>
              </div>
              <div className="footer-copy__link">
                <button
                  type="button"
                  onClick={scrollToTop}
                  className="footer-copy__back-to-top">
                  Back to top
                  <Image
                    src="/static/svg/arrow-gray-up.svg"
                    alt="Back to top"
                    width="14"
                    height="14"
                    unoptimized
                  />
                </button>

                <picture>
                  <source
                    srcSet="/static/img/homepage/webp/logo.webp"
                    type="image/webp"
                  />
                  <source
                    srcSet="/static/img/homepage/logo.png"
                    type="image/jpeg"
                  />
                  <Image
                    src="/static/img/homepage/logo.png"
                    alt="Zoe Logo"
                    className="only-mobile footer-logo"
                    width="100"
                    height="43"
                  />
                </picture>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </>
  );
};

export default LandingPageLayout;
